"use client"

import * as React from "react"
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes"
import { type ThemeProviderProps } from "next-themes"
import { getSharedCookie, setSharedCookie } from "../lib/utils"
import { useSidebarUser, updateUserPreferences } from "../lib/auth"

function ThemeWatcher() {
  const { theme, setTheme } = useTheme()
  const user = useSidebarUser()
  const preferences = user?.preferences
  const hasSyncedRef = React.useRef(false)
  const isSyncingRef = React.useRef(false)

  // 1. Initial hydration: Prioritize DB preferences, then cookies, then default
  React.useEffect(() => {
    if (typeof window === "undefined") return

    const cookieTheme = getSharedCookie("AskFinz_theme")
    const dbTheme = preferences?.theme

    // Helper for robust comparison
    const isEqual = (a?: string, b?: string) => a?.toLowerCase() === b?.toLowerCase()

    // Case A: We have DB preferences. They are the supreme source of truth.
    if (dbTheme) {
        if (!isEqual(dbTheme, theme)) {
            setTheme(dbTheme.toLowerCase())
        }
        hasSyncedRef.current = true // Lock after DB sync
    }
    // Case B: No DB preferences yet, but we have a cookie. Use it as a bridge.
    else if (cookieTheme && !hasSyncedRef.current) {
        if (!isEqual(cookieTheme, theme)) {
            setTheme(cookieTheme.toLowerCase())
        }
        // Don't lock hasSyncedRef yet; wait for potential DB preferences to arrive
    }
    // Case C: First load, no nothing. Mark as potentially synced to avoid loops later.
    else if (!hasSyncedRef.current) {
        // Just mark as ready to watch for changes
        hasSyncedRef.current = true
    }
  }, [preferences, setTheme, theme])

  // 2. Persist theme changes to both DB and cookie
  React.useEffect(() => {
    // Only persist if we have a theme and it's been hydrated/synced at least once
    if (theme && hasSyncedRef.current && !isSyncingRef.current) {
      setSharedCookie("AskFinz_theme", theme)
      
      const dbTheme = preferences?.theme
      const isEqual = (a?: string, b?: string) => a?.toLowerCase() === b?.toLowerCase()

      // Only sync to DB if it differs from current preference to avoid loops
      if (preferences && !isEqual(dbTheme, theme)) {
        isSyncingRef.current = true
        updateUserPreferences({ theme: theme.toLowerCase() }).finally(() => {
            // Small delay before allowing next sync to settle state
            setTimeout(() => {
                isSyncingRef.current = false
            }, 500)
        })
      }
    }
  }, [theme, preferences])

  return null
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider
      {...props}
      enableSystem={true}
      attribute="class"
      storageKey="AskFinz_theme"
    >
      <ThemeWatcher />
      {children}
    </NextThemesProvider>
  )
}

